import {SelectionModel} from '@angular/cdk/collections';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MxCustomizable} from '../../../../mx-customizer';
import {MxNotificationService} from '../../../../shared/services/mx-notification.service';
import {MxButtonModel} from '../../../mx-button/components/mx-button.model';
import {MxGridActionService} from '../../services/mx-grid-actions.service';
import {MxGridConfigStorageService} from '../../services/mx-grid-config-storage.service';
import {GridColumn} from '../../services/grid-column';
import {DefaultGridButtons} from '../../services/mx-grid-enums';
import {SelectionType} from '../../services/selection-type.model';
import {ActivatedRoute, Event, NavigationEnd, Router} from '@angular/router';
import {
    EmptyGridParams,
    MxGridCustomizerEvent,
    MxGridDisableRowFn,
    MxGridGrouping
} from '../../services/mx-grid-customizer-event';
import {MxGridGrandTotalsService} from '../../services/mx-grid-grand-totals.service';
import {MxGridCollectionService} from '../../services/mx-grid-collection.service';
import {MxGridColumnService} from '../../services/mx-grid-column.service';
import {MxGridOrderService} from '../../services/mx-grid-order.service';
import {MxGridPropsService} from '../../services/mx-grid-props.service';
import {Subject} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
import {MX_REFERENCE_SERVICE_TOKEN, MxReferencedEntityService} from '../../services/mx-referenced-entity.service';
import {MatSelect, MatSelectChange} from '@angular/material/select';
import {MatSort, Sort} from '@angular/material/sort';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatOption} from '@angular/material/core';
import {MX_TRANSLATE_SERVICE_TOKEN, MxTranslateService} from '../../../../shared/services/mx-translate.service';

@Component({
    selector: 'mx-grid',
    standalone: false,
    templateUrl: './mx-grid.component.html',
    styleUrls: ['./mx-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        MxGridActionService,
        MxGridGrandTotalsService,
        MxGridCollectionService,
        MxGridColumnService,
        MxGridOrderService,
        MxGridPropsService,
        MxGridConfigStorageService
    ]
})
export class MxGridComponent implements OnInit, AfterViewInit, OnDestroy, MxCustomizable<MxGridCustomizerEvent> {

    readonly CANT_MOVE_IN_READONLY_MODE = "Cannot move in readonly mode";
    readonly MOVE_UP = "Move up";
    readonly MOVE_DOWN = "Move down";

    @ViewChild(MatSort, {static: true}) sort: MatSort;

    @Input() readonly: boolean;
    @Input() isRowsInvisible: boolean;
    gridId: string;

    pageSize: number = this.paginationLimit;
    pageSizes: Array<number> = [5, 10, 15, 25, 50];

    @ViewChild('mySel') skillSel: MatSelect;

    selectAllButton: MxButtonModel = {
        tooltip: 'Select all',
        className: 'mxs-button--round mxs-button--secondary',
        permission: 'all',
        icon: 'select_all',
        clickHandler: () => this.toggleAllSelection()
    };
    deselectAllButton: MxButtonModel = {
        tooltip: 'Deselect all',
        className: 'mxs-button--round mxs-button--primary-cyan',
        permission: 'all',
        icon: 'deselect',
        clickHandler: () => this.toggleAllSelection()
    };

    private disableRowFn: MxGridDisableRowFn;
    public emptyGridParams: EmptyGridParams = () =>  {
        return { image: 'ghost_sad', text: 'There is no data to display yet'};
    }

    groupByColumn: (row: any) => MxGridGrouping;

    reducedGroups: any[] = [];

    initialData: any[] = [];
    initialFlatData: any[] = [];

    /**
     * Initial sorting column name
     */
    public get sortActive(): string {
        return this.propsService.props.sortActive;
    }

    /**
     * Initial sorting direction name
     */
    public get sortDirection(): '' | 'asc' | 'desc' {
        return this.propsService.props.sortDirection;
    }

    @ViewChild(MatPaginator) paginator: MatPaginator;

    /**
     * No need for pagination
     */
    public get noPagination(): boolean {
        return this.propsService.props.noPagination;
    }

    /**
     * Items per page
     */
    protected get paginationLimit(): number {
        return this.propsService.props.paginationLimit;
    }

    get pageIndex(): number {
        return this.propsService.props.pageIndex;
    }

    /**
     * Template to show on grid buttons place
     */
    public get buttonsTemplate(): TemplateRef<any> {
        return this.actionService.buttonsTemplate;
    }

    /**
     * Parameters used to call references report
     */
    private get rowQueryParams(): Array<{[k: string]: any}> {
        return this.propsService.props.rowQueryParams;
    }

    /**
     * Show ShowColumns component
     */
    public get showColumns(): boolean {
        return this.propsService.props.showColumns && this.columnsSelectOptions.length > 1;
    }

    private _destroy$$: Subject<null> = new Subject<null>();

    @Output() customize: EventEmitter<MxGridCustomizerEvent> = new EventEmitter<MxGridCustomizerEvent>();

    @Output() onRowsChanged: EventEmitter<Array<any>> = new EventEmitter<Array<any>>();

    private isInitialLoad: boolean = true;

    public dataSource: MatTableDataSource<any> = new MatTableDataSource();

    public get isGrandTotalsDefined(): boolean {
        return this.grandTotalsService.isGrandTotalsDefined;
    }

    public get gridButtons(): Array<MxButtonModel> {
        return (this.actionService.buttonsTemplate) ? [] : this.actionService.getActions();
    }

    public get selectionMode(): SelectionType {
        return this.propsService.props.selectionMode;
    }

    public get hideButtons(): boolean {
        return (this.propsService.props.readonly || this.readonly) && !this.propsService.props.showButtons;
    }

    public get allowReorder(): boolean {
        return this.propsService.props.allowReorder && this.actionService.isEditAllowed();
    }

    public get allowGroupReorder(): boolean {
        return this.propsService.props.allowGroupReorder && this.actionService.isEditAllowed();
    }

    public get allowSelectAll(): boolean {
        return this.propsService.props.allowSelectAll;
    }

    public get isHeaderHidden(): boolean {
        return this.propsService.props.isHeaderHidden;
    }

    public get selection(): SelectionModel<any> {
        return this.collectionService.selection;
    }

    private get fullSelectionModel(): SelectionModel<any> {
        return this.collectionService.fullSelectionModel;
    }

    public get columnsAll(): ReadonlyArray<GridColumn> {
        return this.columnService.columns;
    }

    public visibleAndControlColumns: Array<string>;

    public get columnsSelectOptions(): ReadonlyArray<{ id, name }> {
        return this.columnService.columnsSelectOptions;
    }

    public getLabel = (label: string): string => {
        return this.translateService.getTranslatedLabel(label);
    };

    constructor(private route: ActivatedRoute,
                private router: Router,
                private actionService: MxGridActionService,
                private grandTotalsService: MxGridGrandTotalsService,
                public collectionService: MxGridCollectionService,
                public columnService: MxGridColumnService,
                private orderService: MxGridOrderService,
                public propsService: MxGridPropsService,
                public storage: MxGridConfigStorageService,
                @Inject(MX_REFERENCE_SERVICE_TOKEN) private referencedEntityService: MxReferencedEntityService,
                @Inject(MX_TRANSLATE_SERVICE_TOKEN) private translateService: MxTranslateService,
                private cd: ChangeDetectorRef,
                private notificationService: MxNotificationService) {
    }

    ngOnInit(): void {
        let event: MxGridCustomizerEvent = new MxGridCustomizerEvent();
        event.customizeRestUrl(this.route.snapshot.data.restLink);
        event.customizePermission(this.route.snapshot.data.permission);
        event.customizeErrorSource(new Subject());
        // showItemDetailsInDialog should be set to true in app.routing for the grids,
        // which are expected to open goToItemDetails form in dialog. In the child route of such grids,
        // the component should be BoObjectFormDialogComponent
        if (this.route.snapshot.data['showItemDetailsInDialog'] !== undefined) {
            event.customizeProps({
                showItemDetailsInDialog: this.route.snapshot.data['showItemDetailsInDialog']
            });
        }
        if (this.route.snapshot.data.tabLabel) {
           event.customizePreloaderConfig({name: 'complex-tabs-preloader'});
        }
        if (this.route.snapshot.data.gridId) {
            event.customizeProps({gridId: this.route.snapshot.data.gridId});
        }
        this.customize.emit(event);

        this.storage.setUp(event);
        this.collectionService.setUp(event);
        this.columnService.setUp(event);
        this.actionService.setUp(event);
        this.orderService.setUp(event);
        this.propsService.setUp(event);
        this.grandTotalsService.setUp(event);

        this.setUp(event);

        // Expose the service API to customizers
        event.notifySetUp(
            {
                actions: this.actionService,
                grandTotals: this.grandTotalsService,
                columns: this.columnService,
                collection: this.collectionService,
                order: this.orderService,
                props: this.propsService,
                reRender: this.rerender,
                storage: this.storage
            });

        if (event.loadRowsOnStart) {
            this.collectionService.doReload();
        } else {
            this.collectionService.setRows([]);
        }

        this.disableRowFn = event.disableRow;

        this.cd.detectChanges();

    }

    private setUp(event: MxGridCustomizerEvent): void {
        this.pageSize = this.propsService.props.paginationLimit;
        if (event.gridProps.showItemDetailsInDialog) {
            let currentRoute = null;
            this.router.events.pipe(
                takeUntil(this._destroy$$),
                filter((routeEvent: Event) => routeEvent instanceof NavigationEnd),
                filter((routeEvent: NavigationEnd) => routeEvent.urlAfterRedirects.split('?')[0] !== currentRoute)
            ).subscribe((routeEvent: NavigationEnd) => {
                currentRoute = routeEvent.urlAfterRedirects.split('?')[0];
                this.collectionService.doReload();
            });
        }

        this.groupByColumn = event.groupByColumn;
        this.collectionService.rowsChanged()
            .pipe(takeUntil(this._destroy$$))
            .subscribe(
                (rows) => {
                    if (this.paginator && this.paginator.pageIndex > 0 && !this.orderService.wasReorder) {
                        this.paginator.pageIndex = 0;
                    }
                    this.orderService.wasReorder = false;
                    this.initialData = rows;
                    this.dataSource.data = this.groupBy();
                    this._enabledRows = this.dataSource.data.filter(row => !row['disabled']);
                    this.updateItemDetailsLink();
                    if (this.isInitialLoad) {
                        this.isInitialLoad = false;
                        this.updatePaginator(rows.length);
                    } else {
                        this.onRowsChanged.emit(rows);
                    }
                    this.collectionService.clearErrors();
                    this.cd.detectChanges();
                }
            );
        this.columnService.columnsSelectControl.valueChanges.pipe(
                takeUntil(this._destroy$$)
            )
            .subscribe(items => {

                if (!items) {
                    this.visibleAndControlColumns = [];
                }
                let visibleAndControlColumns = items.slice();
                if (this.columnService.selectionMode === 'multi') {
                    visibleAndControlColumns.unshift('select');
                }
                if (this.columnService.allowReorder  && this.actionService.isEditAllowed()) {
                    visibleAndControlColumns.push('reorder');
                }
                this.visibleAndControlColumns = visibleAndControlColumns;
            });

        this.columnService.columnsChanged()
            .pipe(takeUntil(this._destroy$$))
            .subscribe(() => this.cd.detectChanges());
        if (event.emptyGridParams) {
            this.emptyGridParams = event.emptyGridParams;
        }
        this.gridId = event.gridProps.gridId;
    }

    groupBy(): any[] {
        const data = this.initialData;

        if (!this.groupByColumn) {
            return data;
        }
        let collapsedGroups = this.reducedGroups;
        if (!this.reducedGroups) {
            collapsedGroups = [];
        }
        const customReducer = (accumulator, currentValue) => {
            const currentGroup = this.groupByColumn(currentValue);
            let key = currentGroup.key;
            let displayValue = currentGroup.displayValue;
            if (!accumulator[key]) {
                accumulator[key] = [
                    {
                        groupName: `${displayValue}`,
                        value: key,
                        infoKey: currentGroup.infoKey,
                        isGroup: true,
                        reduced: collapsedGroups.some(group => group.value === key),
                        invisibleRows: []
                    }
                ];
            }


            if (currentValue.invisible) {
                accumulator[key][0].invisibleRows.push(currentValue)
            }

            accumulator[key].push(currentValue);

            return accumulator;
        };
        let groups = data.reduce(customReducer, []);
        let groupArray = Object.keys(groups).map(key => groups[key]);
        let flatList = groupArray.reduce((a, c) => {
            return a.concat(c);
        }, []);
        this.initialFlatData = flatList;
        const newOrder = flatList.filter(rawLine => !rawLine.isGroup);
        for (let i = 0; i < data.length; i++) {
            if (data[i].id !== newOrder[i].id) {
                this.onRowsChanged.emit(newOrder);
                break;
            }
        }
        return flatList.filter(rawLine => {
            return (
                rawLine.isGroup ||
                collapsedGroups.every(group => this.groupByColumn(rawLine).key !== group.value)
            );
        });
    }

    private updatePaginator(length: number): void {
        if (this.paginator) {
            const pageData = this.storage.getPageData();
            if (!pageData) {
                return;
            }
            const index = pageData.pageIndex * pageData.pageSize + 1 > length ? 0 : pageData.pageIndex;
            this.paginator.pageIndex = index;
            this.paginator.page.next({
                pageIndex: index,
                pageSize: pageData.pageSize,
                length: length
            });
        }
    }

    ngOnDestroy(): void {
        this.orderService.flush();
        this._destroy$$.next(null);
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.cd.detectChanges();
    }

    private updateItemDetailsLink(): void {
        const props = this.propsService.props;
        if (props.isGoToItemDetailsAllowed && !props.detailWithParams) {
            const linkColumn = this.columnsAll.find(column => !!column.idProp);
            if ( !!linkColumn ) {
                this.dataSource.data.forEach(row => row['linkProp'] = this.collectionService.getItemDetailsLink(row[linkColumn.idProp]));
            }
        }
    }

    private absoluteIndex(index: number): number {
        let absoluteIndex = index;
        if (this.groupByColumn) {
            for (let i = 0; i < index; i++) {
                if (this.dataSource.data[i].isGroup) {
                    absoluteIndex--;
                }
            }
        }
        if (this.paginator) {
            return absoluteIndex + this.paginator.pageIndex * this.paginator.pageSize;
        } else {
            return absoluteIndex;
        }
    }

    public disableUpButton(rowIndex: number): boolean {
        let compareNumber = 0;
        if (this.groupByColumn) {
            for (let i = rowIndex; i >= compareNumber; i--) {
                if (this.dataSource.data[i].isGroup) {
                    compareNumber = i + 1;
                    break;
                }
            }
        }
        return (rowIndex === compareNumber || this.propsService.props.readonly || this.readonly);
    }

    public disableDownButton(rowIndex: number): boolean {
        let compareNumber = this.dataSource.data.length - 1;
        if (this.groupByColumn) {
                for (let i = rowIndex; i < compareNumber; i++) {
                    if (this.dataSource.data[i].isGroup) {
                        compareNumber = i - 1;
                        break;
                    }
                }
        }
        return (rowIndex === compareNumber || this.propsService.props.readonly || this.readonly);
    }

    private orderWithPageSaving(orderFunc: () => void): void {
        if (!this.paginator) {
            orderFunc();
            return;
        }
        let selectedPage = this.paginator.pageIndex;
        let {length = 0, pageSize = 0} = {...this.paginator};
        orderFunc();
        this.paginator.pageIndex = selectedPage;
        this.paginator.page.next({
            pageIndex: selectedPage,
            length: length,
            pageSize: pageSize
        });
    }

    public onOrderUp = (rowIndex: number): void => {
        this.orderService.wasReorder = true;
        this.orderWithPageSaving(() => this.orderService.onOrderUp(this.absoluteIndex(rowIndex)));
    }

    public onOrderDown = (rowIndex: number): void => {
        this.orderService.wasReorder = true;
        this.orderWithPageSaving(() => this.orderService.onOrderDown(this.absoluteIndex(rowIndex)));

    }

    private isSelectable(): boolean {
        return this.selectionMode !== 'none';
    }
    _enabledRows: any[] = [];

    public isAllDisabled(): boolean {
        return this._enabledRows.length === 0;
    }

    public isAllSelected(): boolean {
        // TODO move to gridCollection service
        let enabled = this.dataSource.data.filter(row => !row['disabled']).length;
        let selectedEnabled = this.selection.selected.filter(row => !row['disabled']).length;
        return enabled > 0 ? enabled === selectedEnabled : this.dataSource.data.length === this.selection.selected.length;
    }

    public masterToggle(): void {
        // TODO move to gridCollection service
        let enabledRows = this._enabledRows;
        if (this.isAllSelected()) {
            if (this.disableRowFn) {
                enabledRows.forEach(row => {
                        row['disabled'] = this.disableRowFn(row, this.fullSelectionModel);
                });
            }
            this.selection.deselect(...enabledRows.filter(value => !value.disabled));
        } else {
            this.selection.deselect(enabledRows);
            if (this.disableRowFn) {
                enabledRows.forEach(row => {
                        row['disabled'] = this.disableRowFn(row, this.fullSelectionModel);
                });
            }
            this.selection.select(...enabledRows.filter(value => !value.disabled));
        }
    }

    public isRowWithError(index: number): boolean {
        return this.collectionService.isRowWithError(index);
    }

    public isRowBold(row: any): boolean {
        return this.collectionService.isRowBold(row);
    }

    public isRowInvisible(row: any): boolean {
        if (this.isRowsInvisible) {
            return row.invisible;
        }
    }

    public isRowDisabled(row: any): boolean {
        return row.disabled !== null;
    }

    public getErrorByIndex(index: number, column: string): string | boolean {
        return this.collectionService.getErrorByIndex(index, column);
    }

    public getDisabledMessage(row: any): string {
        row.disabled = null;
        if (this.disableRowFn) {
            row.disabled = this.disableRowFn(row, this.fullSelectionModel, this.collectionService);
        }
        return row.disabled;
    }

    public onRowClick(_event: MouseEvent, row: any): void {
        if (this.isSelectable()) {
            if (this.selectionMode === 'single' && !this.selection.isSelected(row)) {
                this.selection.clear();
                this.fullSelectionModel.clear();
            }

            this.selection.toggle(row);
        }
    }

    public onRowDblClick(_event: MouseEvent, row: any): void {
        let allowedAction = this.actionService.getActionAllowedForDblClick([DefaultGridButtons.edit, DefaultGridButtons.view]);
        if (!allowedAction) {
            return;
        }
        this.collectionService.selection.clear();
        this.collectionService.selection.select(row);
        if (this.isSelectable() && this.propsService.props.isGoToItemDetailsAllowed && allowedAction) {
            this.actionService.runAction(allowedAction as string, this.collectionService.selection);
        }
    }

    private getGridColumn(name: string): GridColumn {
        return this.columnService.columnsAllByName.get(name);
    }

    public getColumnGrandTotal(prop: string): number | string {
        return this.grandTotalsService.grandTotals.get(prop);
    }

    public getQueryParams(columnProp: string, rowIndex: number): {[k: string]: any} {
        let absoluteIndex = this.absoluteIndex(rowIndex);
        let currentRow = this.rowQueryParams[absoluteIndex];
        if (currentRow) {
            return currentRow[columnProp];
        }
        return {};
    }

    public useReferencedCellTemplate(column: GridColumn): boolean {
        return column.referencedReport || (column.referencedEntity && this.hasAccess(column.referencedEntity.entityName));
    }

    public hasAccess(entityName: string): boolean {
        return this.referencedEntityService.hasAccess(entityName);
    }

    public getReferencedEntityUrl(column: GridColumn, row: any): string {
        let url = this.referencedEntityService.getReferencedEntityMapping(column.referencedEntity.entityName, row);
        for (let property in column.referencedEntity.columnToKeyMap) {
            let rowCol = Object.keys(row).find(value => value.toLowerCase() === property.toLowerCase());
            if (rowCol) {
                url = url.replace(':' + column.referencedEntity.columnToKeyMap[property], row[rowCol]);
            } else {
                url = url.replace(':' + property, column.referencedEntity.columnToKeyMap[property]);
            }
        }
        return url;
    }

    public getReferencedEntityQuery(column: GridColumn): any {
        let queryParam = this.referencedEntityService.getReferencedEntityFilter(column.referencedEntity.entityName);
        if (queryParam) {
            return {
                [queryParam]: column.referencedEntity.filterValue
            };
        } else {
            return;
        }

    }

    public getGridColumnDateFormat(name: string): string {
        let gridColumn = this.getGridColumn(name);
        return gridColumn.dateTimeFormat ? gridColumn.dateTimeFormat : this.propsService.props.dateFormat;
    }

    public getThumbnailClass(row: any): string {
        return this.collectionService.getThumbnailClass(row);
    }

    public getThumbnailUrl(row: any): string {
        return this.collectionService.getThumbnailUrl(row);
    }

    public getThumbnailColumnName(): string {
        return this.collectionService.getThumbnailColumnName();
    }

    public showReferenceReportFailure(reportName: string): void {
        this.notificationService.showFailure(`Cannot find the report "${reportName}"`);
    }

    public setPageSize = (matSelectChange: MatSelectChange) => {
        this.pageSize = matSelectChange.value;
        this.paginator._changePageSize(this.pageSize);
    }

    private rerender = () => {
        this.cd.detectChanges();
    }

    isGroup(index: number, item: any): boolean {
        return item.isGroup;
    }

    reduceGroup(row: any): void {
        row.reduced = !row.reduced;
        if (row.reduced) {
            this.reducedGroups.push(row);
        } else {
            this.reducedGroups = this.reducedGroups.filter(
                el => el.value !== row.value
            );
        }

        this.dataSource.data = this.groupBy();
    }

    columnProp(index: number, item: { prop: string }): string {
        return item.prop;
    }

    columnId(index: number, item: { id: number, name: string }): number {
        return item.id;
    }
    getNumberFormat(column: GridColumn): string {
        if (column.precision) {
            return `1.${column.precision}-${column.precision}`;
        } else {
            return null;
        }
    }

    saveSort($event: Sort): void {
        this.storage.saveSort($event);
    }

    pageSelected($event: PageEvent): void {
        this.storage.savePage({
            pageIndex: $event.pageIndex,
            pageSize: $event.pageSize,
            length: $event.length
        });
    }

    disableGroupUpButton(rowIndex: number): boolean {
        return (rowIndex === 0 || this.propsService.props.readonly || this.readonly);
    }

    disableGroupDownButton(rowIndex: number): boolean {
        if (!this.groupByColumn) {
            return true;
        }
        const isLastGroup = this.dataSource.data.map(item => item.isGroup).lastIndexOf(true) === rowIndex;

        return (isLastGroup || this.propsService.props.readonly || this.readonly);
    }

    onOrderGroupUp(rowIndex: number): void {
        this.orderService.wasReorder = true;
        this.orderService.onOrderGroup('UP', rowIndex, this.initialFlatData);
    }

    onOrderGroupDown(rowIndex: number): void {
        this.orderService.wasReorder = true;
        this.orderService.onOrderGroup('DOWN', rowIndex, this.initialFlatData);
    }

    getPath(referencedReport: string): string {
        return this.referencedEntityService.getReferencedReportMapping(referencedReport) || '../';
    }

    isDeselectedOption(): boolean {
        return this.skillSel === undefined || this.skillSel.options.some((item: MatOption) => !item.selected);
    }

    toggleAllSelection() {
        let selectedCount = this.skillSel.options.filter((item:MatOption) => item.selected).length;

        if (selectedCount < this.columnsSelectOptions.length) {
            this.skillSel.options.forEach((item: MatOption) => item.select());
        } else {
            this.skillSel.options.forEach((item: MatOption) => {
                item.deselect();
            });
        }
        this.skillSel.close();
    }

    filterOptionsIfContainSearchParam(optionName: string, searchValue: string): boolean {
        return optionName.toLowerCase().includes(searchValue.toLowerCase());
    }
}
